import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './theme';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FileUploader from './components/FileUploader';
import EdiViewer from './components/EdiViewer';
import './App.css';

// Premium EDI-Z Logo - The centerpiece of your application
const Logo = () => {
  return (
    <Box 
      component="div" 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 4,
        position: 'relative'
      }}
    >
      {/* Main Logo Container with Enhanced Glass Morphism */}
      <Box
        component="div"
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          mb: 4,
          p: 5,
          borderRadius: 6,
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(40px)',
          border: '2px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          boxShadow: '0 35px 70px rgba(0, 0, 0, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.1)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.03)',
            boxShadow: '0 50px 100px rgba(0, 0, 0, 0.25), inset 0 2px 0 rgba(255, 255, 255, 0.15)',
            borderColor: 'rgba(59, 130, 246, 0.3)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(168, 85, 247, 0.15) 30%, rgba(16, 185, 129, 0.15) 70%, rgba(6, 182, 212, 0.15) 100%)',
            opacity: 0,
            transition: 'opacity 0.5s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
          // Floating particles effect
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: 'radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
              '33%': { transform: 'translate(10px, -10px) rotate(120deg)' },
              '66%': { transform: 'translate(-5px, 5px) rotate(240deg)' }
            }
          }
        }}
      >
        {/* Enhanced EDI Logo */}
        <Box
          component="div"
          sx={{
            width: 140,
            height: 140,
            borderRadius: 5,
            background: 'linear-gradient(145deg, #1e3a8a 0%, #3b82f6 25%, #06b6d4 50%, #0891b2 75%, #0f766e 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 4,
            boxShadow: '0 20px 40px rgba(59, 130, 246, 0.5), inset 0 3px 6px rgba(255, 255, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.5s ease',
            '&:hover': {
              transform: 'rotate(-3deg) scale(1.05)',
              boxShadow: '0 30px 60px rgba(59, 130, 246, 0.6), inset 0 3px 6px rgba(255, 255, 255, 0.3)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.2) 30%, rgba(255, 255, 255, 0.1) 70%, transparent 100%)',
              transform: 'translateX(-100%)',
              transition: 'transform 0.6s ease',
            },
            '&:hover::before': {
              transform: 'translateX(100%)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -3,
              left: -3,
              right: -3,
              bottom: -3,
              background: 'linear-gradient(145deg, #3b82f6, #06b6d4, #10b981, #8b5cf6)',
              borderRadius: 5,
              zIndex: -1,
              filter: 'blur(12px)',
              opacity: 0.7,
              animation: 'glow 3s ease-in-out infinite alternate',
              '@keyframes glow': {
                '0%': { opacity: 0.5, filter: 'blur(12px)' },
                '100%': { opacity: 0.8, filter: 'blur(16px)' }
              }
            }
          }}
        >
          <Typography 
            component="span" 
            sx={{ 
              color: 'white',
              fontWeight: 900,
              fontSize: '3rem',
              position: 'relative',
              zIndex: 1,
              textShadow: '0 3px 12px rgba(0, 0, 0, 0.4)',
              letterSpacing: '-0.05em',
              mb: 0.5
            }}
          >
            EDI
          </Typography>
          <Box sx={{
            width: 40,
            height: 2,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
            borderRadius: 1,
            animation: 'scan 2s infinite',
            '@keyframes scan': {
              '0%': { transform: 'scaleX(0)' },
              '50%': { transform: 'scaleX(1)' },
              '100%': { transform: 'scaleX(0)' }
            }
          }} />
        </Box>
        
        {/* Enhanced Z Symbol with Lightning */}
        <Box 
          component="div"
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #10b981 0%, #059669 30%, #047857 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 15px 30px rgba(16, 185, 129, 0.5), inset 0 3px 6px rgba(255, 255, 255, 0.25)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.5s ease',
            animation: 'energyPulse 3s infinite',
            '@keyframes energyPulse': {
              '0%, 100%': { 
                boxShadow: '0 15px 30px rgba(16, 185, 129, 0.5), inset 0 3px 6px rgba(255, 255, 255, 0.25)',
                transform: 'scale(1)'
              },
              '50%': { 
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.7), inset 0 3px 6px rgba(255, 255, 255, 0.35)',
                transform: 'scale(1.05)'
              }
            },
            '&:hover': {
              transform: 'rotate(15deg) scale(1.15)',
              animation: 'none',
              boxShadow: '0 25px 50px rgba(16, 185, 129, 0.8), inset 0 3px 6px rgba(255, 255, 255, 0.4)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
              borderRadius: '50%',
              animation: 'spin 4s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }
          }}
        >
          {/* Enhanced Lightning Icon */}
          <Box sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path 
                d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" 
                fill="white" 
                style={{
                  filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4))'
                }}
              />
            </svg>
          </Box>
          
          {/* Z Letter overlay for branding */}
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              fontSize: '0.75rem',
              fontWeight: 800,
              color: 'white',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
              zIndex: 3
            }}
          >
            Z
          </Typography>
        </Box>
        
        {/* Enhanced Connecting Animation */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 250,
          height: 3,
          background: 'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 10%, rgba(16, 185, 129, 0.6) 50%, rgba(168, 85, 247, 0.3) 90%, transparent 100%)',
          borderRadius: 2,
          zIndex: 0,
          animation: 'dataFlow 4s infinite',
          '@keyframes dataFlow': {
            '0%': { 
              opacity: 0.3,
              transform: 'translate(-50%, -50%) scaleX(0.5)'
            },
            '50%': { 
              opacity: 1,
              transform: 'translate(-50%, -50%) scaleX(1.2)'
            },
            '100%': { 
              opacity: 0.3,
              transform: 'translate(-50%, -50%) scaleX(0.5)'
            }
          }
        }} />
        
        {/* Data particles */}
        <Box sx={{
          position: 'absolute',
          top: '45%',
          left: '35%',
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: '#3b82f6',
          animation: 'particle1 3s infinite',
          '@keyframes particle1': {
            '0%': { transform: 'translateX(0)', opacity: 0 },
            '50%': { opacity: 1 },
            '100%': { transform: 'translateX(100px)', opacity: 0 }
          }
        }} />
        <Box sx={{
          position: 'absolute',
          top: '55%',
          left: '40%',
          width: 3,
          height: 3,
          borderRadius: '50%',
          background: '#10b981',
          animation: 'particle2 3s infinite 0.5s',
          '@keyframes particle2': {
            '0%': { transform: 'translateX(0)', opacity: 0 },
            '50%': { opacity: 1 },
            '100%': { transform: 'translateX(80px)', opacity: 0 }
          }
        }} />
      </Box>
      
      {/* Brand Name */}
      <Typography 
        variant="h3"
        component="h1"
        sx={{ 
          fontWeight: 900,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 50%, #10b981 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          letterSpacing: '-0.02em',
          mb: 2,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 60,
            height: 3,
            background: 'linear-gradient(90deg, #3b82f6, #10b981)',
            borderRadius: 2,
            animation: 'underlineGlow 2s infinite alternate',
            '@keyframes underlineGlow': {
              '0%': { boxShadow: '0 0 5px rgba(59, 130, 246, 0.5)' },
              '100%': { boxShadow: '0 0 15px rgba(16, 185, 129, 0.8)' }
            }
          }
        }}
      >
        EDI-Z
      </Typography>
      
      <Typography 
        variant="h6" 
        sx={{ 
          fontSize: '1.2rem',
          fontWeight: 500,
          color: 'text.secondary',
          maxWidth: '650px',
          mx: 'auto',
          lineHeight: 1.6,
          textAlign: 'center',
          opacity: 0.9
        }}
      >
        Transform complex EANCOM/EDIFACT data into clear, readable information instantly
      </Typography>
    </Box>
  );
};

function App() {
  const [ediContent, setEdiContent] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('dark');
  const appTheme = createAppTheme(mode);

  const handleFileUpload = (content) => {
    setEdiContent(content);
    setIsLoading(true);
    // We'll implement the parser in a separate file
    import('./utils/eancomParser').then(module => {
      const parser = module.default;
      try {
        const result = parser(content);
        setParsedData(result);
      } catch (error) {
        console.error('Error parsing EDI file:', error);
        // We could handle errors more gracefully here
      } finally {
        setIsLoading(false);
      }
    });
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              py: 5,
              mb: 4, 
              borderBottom: '3px solid',
              borderColor: 'primary.main',
              textAlign: 'center',
              borderRadius: '18px 18px 4px 4px',
              position: 'relative',
              background: mode === 'light' ? 'linear-gradient(180deg, #ffffff 0%, rgba(247, 249, 252, 0.5) 100%)' : 'linear-gradient(180deg, rgba(18,26,43,0.95) 0%, rgba(18,26,43,0.7) 100%)',
              boxShadow: '0 6px 20px rgba(63, 81, 181, 0.06)'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              right: 16, 
              top: 16,
              zIndex: 10 
            }}>
              <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                <IconButton 
                  color="inherit" 
                  onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
                  sx={{
                    width: 56,
                    height: 56,
                    padding: 2,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    }
                  }}
                >
                  {mode === 'light' ? <DarkModeIcon sx={{ fontSize: 28 }} /> : <LightModeIcon sx={{ fontSize: 28 }} />}
                </IconButton>
              </Tooltip>
            </Box>
            <Logo />
          </Paper>
          
          <FileUploader onFileUpload={handleFileUpload} />
          
          {ediContent && (
            <Paper 
              elevation={3} 
              sx={{ mt: 4, overflow: 'hidden', borderRadius: '12px' }}
            >
              <EdiViewer 
                ediContent={ediContent} 
                parsedData={parsedData} 
                isLoading={isLoading} 
              />
            </Paper>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App; 